import { useParams, Link, Navigate } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import { findProductBySlug, products } from "../mock/mock";
import { ArrowRight, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { company } from "../mock/mock";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = findProductBySlug(slug);
  if (!product) return <Navigate to="/products" replace />;

  const others = products.filter((p) => p.slug !== slug).slice(0, 4);

  return (
    <div>
      <PageBanner
        title={product.name}
        subtitle={product.short}
        breadcrumbs={[{ label: "Products", to: "/products" }, { label: product.name }]}
      />

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-3 gap-10">
          <aside className="lg:col-span-1">
            <div className="sticky top-28">
              <div className="bg-[#0d2240] text-white rounded-xl p-5">
                <h4 className="font-heading font-bold text-base uppercase tracking-widest border-b border-white/15 pb-3">All Products</h4>
                <ul className="mt-3 max-h-[420px] overflow-y-auto mega-scroll pr-1">
                  {products.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/products/${p.slug}`}
                        className={`block py-2 px-3 rounded-md text-[13px] ${
                          p.slug === product.slug
                            ? "bg-[#c8102e] text-white"
                            : "text-white/80 hover:bg-white/10"
                        }`}
                      >
                        {p.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-5 bg-white border border-gray-100 rounded-xl p-5">
                <h4 className="font-heading font-bold text-[#0d2240]">Need Help Selecting?</h4>
                <p className="text-sm text-gray-600 mt-1">Talk to our experts now.</p>
                <a href={`tel:+91${company.phone}`} className="mt-3 inline-flex items-center gap-2 text-[#c8102e] font-semibold text-sm">
                  <Phone size={14} /> +91 {company.phone}
                </a>
                <a href={`https://wa.me/91${company.whatsapp}`} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-2 text-[#25D366] font-semibold text-sm">
                  <MessageCircle size={14} /> Chat on WhatsApp
                </a>
              </div>
            </div>
          </aside>
          <div className="lg:col-span-2">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="w-full md:w-72 shrink-0 rounded-xl overflow-hidden border border-gray-100">
                <img src={product.image} alt={product.name} className="w-full h-72 object-cover" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0d2240] section-title">
                  {product.name}
                </h2>
                <p className="text-gray-600 leading-relaxed mt-5">{product.description}</p>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {["High quality manufacturing", "Multiple sizes available", "Custom widths on order", "Bulk wholesale pricing"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#0d2240]">
                      <CheckCircle2 size={16} className="text-[#c8102e] mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2 mt-6">
                  <a href={`mailto:${company.email}?subject=Inquiry%20about%20${encodeURIComponent(product.name)}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#c8102e] hover:bg-[#9a0c24] text-white text-sm font-semibold">
                    Request Quote <ArrowRight size={14} />
                  </a>
                  <a href={`https://wa.me/91${company.whatsapp}?text=Hello%20Mahalaxmi%20Tape%20Industries%2C%20I%20am%20interested%20in%20${encodeURIComponent(product.name)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-semibold">
                    <MessageCircle size={14} /> WhatsApp Inquiry
                  </a>
                </div>
              </div>
            </div>

            {/* Sub-products */}
            <div className="mt-12">
              <h3 className="font-heading font-bold text-xl text-[#0d2240] mb-5">
                {product.name} Variants ({product.subProducts.length})
              </h3>
              <div className="grid sm:grid-cols-2 gap-5">
                {product.subProducts.map((s) => (
                  <Link
                    key={s.id}
                    to={`/products/${product.slug}/${s.slug}`}
                    className="card-lift group flex gap-4 bg-white p-3 rounded-xl border border-gray-100"
                  >
                    <div className="w-28 h-28 shrink-0 rounded-lg overflow-hidden bg-[#f7f7f9]">
                      <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-heading font-semibold text-[#0d2240] text-[15px] group-hover:text-[#c8102e] transition-colors">
                        {s.name}
                      </h4>
                      <p className="text-[13px] text-gray-600 mt-1 line-clamp-2">{s.short}</p>
                      <span className="inline-flex items-center gap-1 text-[#c8102e] text-[12.5px] font-semibold mt-2">
                        View Details <ArrowRight size={12} />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Products */}
      <section className="py-12 bg-[#f7f7f9]">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="font-heading font-bold text-2xl text-[#0d2240] mb-6">Explore Other Products</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((p) => (
              <Link key={p.id} to={`/products/${p.slug}`} className="card-lift group bg-white rounded-lg overflow-hidden border border-gray-100">
                <div className="h-40 overflow-hidden bg-[#f7f7f9]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                </div>
                <div className="p-4">
                  <h4 className="font-heading font-semibold text-[#0d2240] group-hover:text-[#c8102e] transition-colors">{p.name}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
